import { createSystem, defaultConfig, defineConfig } from '@chakra-ui/react'
import { buttonRecipe } from './components/Button.recipes'

const config = defineConfig({
  conditions: {
    // Custom condition selector for dark mode using data-theme attribute
    dark: '[data-theme="dark"] &',
    light: '[data-theme="light"] &',
  },
  theme: {
    recipes: {
      button: buttonRecipe,
    },
    tokens: {
      colors: {
        // Primary: Grass Green
        green: {
          50: { value: '#f0f9f3' },
          100: { value: '#dcf4e6' },
          200: { value: '#bbe8d0' },
          300: { value: '#8dd8b3' },
          400: { value: '#56c18e' },
          500: { value: '#34a374' }, // Main grass green
          600: { value: '#22885e' },
          700: { value: '#1e6b4b' },
          800: { value: '#1d5540' },
          900: { value: '#1a4635' },
        },
        // Secondary: Soft Sky Blue
        blue: {
          50: { value: '#f0f7ff' },
          100: { value: '#e0efff' },
          200: { value: '#bae0ff' },
          300: { value: '#87ccff' },
          400: { value: '#4db5ff' }, // Main sky blue
          500: { value: '#2d9cff' },
          600: { value: '#1b82f5' },
          700: { value: '#1569d1' },
          800: { value: '#1755a8' },
          900: { value: '#194683' },
        },
        // Calming neutrals
        gray: {
          50: { value: '#fafafa' },
          100: { value: '#f5f5f5' },
          200: { value: '#e8e8e8' },
          300: { value: '#d1d1d1' },
          400: { value: '#b0b0b0' },
          500: { value: '#888888' },
          600: { value: '#6d6d6d' },
          700: { value: '#5a5a5a' },
          800: { value: '#3d3d3d' },
          900: { value: '#2d2d2d' },
        },
        // Wellness-specific colors
        wellness: {
          mint: { value: '#e8f5f0' },
          sage: { value: '#9caf9c' },
          cream: { value: '#fefdf8' },
          lavender: { value: '#f3f1ff' },
          peach: { value: '#fff4f0' },
        },
        // V3 Legacy colors
        v3: {
          green: { value: '#b2c230' },
          greenHover: { value: '#a1af2a' },
        },
        // Brand colors mapped to semantic names
        brand: {
          50: { value: '{colors.green.50}' },
          100: { value: '{colors.green.100}' },
          200: { value: '{colors.green.200}' },
          300: { value: '{colors.green.300}' },
          400: { value: '{colors.green.400}' },
          500: { value: '{colors.green.500}' },
          600: { value: '{colors.green.600}' },
          700: { value: '{colors.green.700}' },
          800: { value: '{colors.green.800}' },
          900: { value: '{colors.green.900}' },
        },
        accent: {
          50: { value: '{colors.blue.50}' },
          100: { value: '{colors.blue.100}' },
          200: { value: '{colors.blue.200}' },
          300: { value: '{colors.blue.300}' },
          400: { value: '{colors.blue.400}' },
          500: { value: '{colors.blue.500}' },
          600: { value: '{colors.blue.600}' },
          700: { value: '{colors.blue.700}' },
          800: { value: '{colors.blue.800}' },
          900: { value: '{colors.blue.900}' },
        },
      },
      fonts: {
        heading: { value: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' },
        body: { value: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' },
        brand: { value: '"Nunito", "Quicksand", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' },
        alteHaas: { value: '"Alte Haas Grotesk", Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' },
      },
      fontWeights: {
        normal: { value: '400' },
        medium: { value: '500' },
        semibold: { value: '600' },
        bold: { value: '700' },
      },
      lineHeights: {
        shorter: { value: '1.25' },
        short: { value: '1.375' },
        base: { value: '1.5' },
        tall: { value: '1.625' },
        taller: { value: '2' },
      },
      letterSpacings: {
        tighter: { value: '-0.05em' },
        tight: { value: '-0.025em' },
        normal: { value: '0' },
        wide: { value: '0.025em' },
        wider: { value: '0.05em' },
        widest: { value: '0.1em' },
      },
      radii: {
        none: { value: '0' },
        sm: { value: '0.125rem' },
        base: { value: '0.375rem' },
        md: { value: '0.5rem' },
        lg: { value: '0.75rem' },
        xl: { value: '1rem' },
        '2xl': { value: '1.5rem' },
        '3xl': { value: '2rem' },
        full: { value: '9999px' },
      },
      shadows: {
        xs: { value: '0 0 0 1px rgba(0, 0, 0, 0.05)' },
        sm: { value: '0 1px 2px 0 rgba(0, 0, 0, 0.03)' },
        base: { value: '0 1px 3px 0 rgba(0, 0, 0, 0.06), 0 1px 2px 0 rgba(0, 0, 0, 0.04)' },
        md: { value: '0 4px 6px -1px rgba(0, 0, 0, 0.06), 0 2px 4px -1px rgba(0, 0, 0, 0.03)' },
        lg: { value: '0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -2px rgba(0, 0, 0, 0.04)' },
        xl: { value: '0 20px 25px -5px rgba(0, 0, 0, 0.12), 0 10px 10px -5px rgba(0, 0, 0, 0.06)' },
        '2xl': { value: '0 25px 50px -12px rgba(0, 0, 0, 0.18)' },
        inner: { value: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.04)' },
      },
      zIndex: {
        hide: { value: -1 },
        base: { value: 0 },
        docked: { value: 10 },
        dropdown: { value: 1000 },
        sticky: { value: 1100 },
        banner: { value: 1200 },
        overlay: { value: 1300 },
        modal: { value: 1400 },
        popover: { value: 1500 },
        skipLink: { value: 1600 },
        toast: { value: 1700 },
        tooltip: { value: 1800 },
      },
    },
    semanticTokens: {
      colors: {
        // Background colors - use condition-based values
        bg: {
          DEFAULT: {
            value: {
              base: '{colors.wellness.cream}',
              dark: '{colors.gray.900}'
            }
          },
          subtle: {
            value: {
              base: '{colors.gray.50}',
              dark: '{colors.gray.800}'
            }
          },
          muted: {
            value: {
              base: '{colors.gray.100}',
              dark: '{colors.gray.700}'
            }
          },
        },
        // Text colors - use condition-based values
        fg: {
          DEFAULT: {
            value: {
              base: '{colors.gray.800}',
              dark: '{colors.gray.100}'
            }
          },
          muted: {
            value: {
              base: '{colors.gray.600}',
              dark: '{colors.gray.400}'
            }
          },
          subtle: {
            value: {
              base: '{colors.gray.500}',
              dark: '{colors.gray.500}'
            }
          },
        },
        // Border colors - use condition-based values with better visibility in dark mode
        border: {
          DEFAULT: {
            value: {
              base: '{colors.gray.200}',
              dark: '{colors.gray.600}' // More visible in dark mode (was gray.700)
            }
          },
          muted: {
            value: {
              base: '{colors.gray.100}',
              dark: '{colors.gray.700}' // More visible in dark mode (was gray.800)
            }
          },
        },
      },
    },
    textStyles: {
      heading: {
        fontFamily: 'heading',
        fontWeight: 'semibold',
        lineHeight: 'shorter',
        letterSpacing: 'tight',
      },
      body: {
        fontFamily: 'body',
        fontWeight: 'normal',
        lineHeight: 'base',
      },
      alteHaasBold: {
        fontFamily: 'alteHaas',
        fontWeight: 'bold',
        lineHeight: 'base',
      },
    },
  },
  globalCss: {
    html: {
      // Set default color palette so Chakra components (Button, Tag, etc.) pick up brand colors automatically
      colorPalette: 'brand',
    },
    'html, body': {
      bg: 'bg',
      color: 'fg',
      fontFamily: 'body',
      lineHeight: 'base',
    },
    '*::placeholder': {
      opacity: 1,
      color: 'fg.muted',
    },
    // Merge global selectors under a single rule to avoid duplicate keys
    '*, *::before, *::after': {
      borderColor: 'border',
    },
    // Navigation elements - use semantic tokens for theme-aware styling
    '[data-nav]': {
      bg: 'bg',
      borderColor: 'border',
    },
    // Header elements - use semantic tokens for theme-aware styling
    '[data-header]': {
      bg: 'bg',
      color: 'fg', // Use semantic token for theme-aware text color
      borderColor: 'border',
    },
    // Ensure form controls are selectable
    'input, textarea, select, [contenteditable]': {
      userSelect: 'auto !important',
    },
  },
})

export const system = createSystem(defaultConfig, config) 